import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  faArrowRight,
  faEdit,
  faExternalLinkAlt,
  faGripVertical,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState, useEffect } from "react";
import { toast } from "sonner";

// Custom spinner component
const Spinner = ({ size = "lg" }: { size?: string }) => (
  <div
    className={`animate-spin rounded-full border-2 border-current border-t-transparent ${
      size === "lg" ? "h-8 w-8" : "h-4 w-4"
    }`}
  />
);

interface ListicleItem {
  id: string;
  name: string;
  website: string;
  description?: string;
}

interface EditableListicleItem extends ListicleItem {
  selected: boolean;
  isEditing?: boolean;
}

interface ListicleItemsListProps {
  items: ListicleItem[];
  onGenerateArticle: (selectedItems: ListicleItem[]) => void;
  loading: boolean;
  loadingMessage: string;
}

export function ListicleItemsList({
  items,
  onGenerateArticle,
  loading,
  loadingMessage,
}: ListicleItemsListProps) {
  const [editableItems, setEditableItems] = useState<EditableListicleItem[]>(
    () =>
      items.map((item) => ({
        ...item,
        selected: true,
      }))
  );

  const [newItemName, setNewItemName] = useState("");
  const [newItemWebsite, setNewItemWebsite] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);

  // Handle select all/none
  const handleSelectAll = (checked: boolean) => {
    setEditableItems((prev) =>
      prev.map((item) => ({
        ...item,
        selected: checked,
      }))
    );
  };

  // Handle individual item selection
  const handleItemSelect = (index: number, checked: boolean) => {
    setEditableItems((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], selected: checked };
      return updated;
    });
  };

  // Handle item removal
  const handleRemoveItem = (index: number) => {
    setEditableItems((prev) => prev.filter((_, i) => i !== index));
  };

  // Handle item editing
  const handleEditItem = (index: number, field: "name" | "website", value: string) => {
    setEditableItems((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  // Toggle edit mode for an item
  const toggleEditMode = (index: number) => {
    setEditableItems((prev) => {
      const updated = [...prev];
      updated[index] = { 
        ...updated[index], 
        isEditing: !updated[index].isEditing 
      };
      return updated;
    });
  };

  // Handle adding new item
  const handleAddItem = () => {
    if (!newItemName.trim() || !newItemWebsite.trim()) {
      toast.error("Please enter both name and website");
      return;
    }

    // Basic URL validation
    let website = newItemWebsite.trim();
    if (!website.startsWith("http://") && !website.startsWith("https://")) {
      website = "https://" + website;
    }

    const newItem: EditableListicleItem = {
      id: `custom_${Date.now()}`,
      name: newItemName.trim(),
      website,
      description: `Custom addition to the list`,
      selected: true,
    };

    setEditableItems((prev) => [...prev, newItem]);
    setNewItemName("");
    setNewItemWebsite("");
    setShowAddForm(false);
    toast.success("Item added successfully");
  };

  // Handle article generation
  const handleGenerate = () => {
    const selectedItems = editableItems.filter((item) => item.selected);
    
    if (selectedItems.length < 3) {
      toast.error("Please select at least 3 items to create a listicle");
      return;
    }

    if (selectedItems.length > 15) {
      toast.error("Please select no more than 15 items for optimal readability");
      return;
    }

    onGenerateArticle(selectedItems);
  };

  const selectedCount = editableItems.filter((item) => item.selected).length;
  const allSelected = selectedCount === editableItems.length && editableItems.length > 0;
  const someSelected = selectedCount > 0 && selectedCount < editableItems.length;

  // Handle indeterminate state for select all checkbox
  useEffect(() => {
    const selectAllCheckbox = document.getElementById("select-all") as HTMLInputElement;
    if (selectAllCheckbox) {
      selectAllCheckbox.indeterminate = someSelected;
    }
  }, [someSelected]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Spinner size="lg" />
        <span className="ml-2">{loadingMessage}</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with select all and stats */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Checkbox
            id="select-all"
            checked={allSelected}
            onCheckedChange={handleSelectAll}
          />
          <Label htmlFor="select-all" className="font-medium">
            Select All
          </Label>
        </div>
        <div className="text-sm text-muted-foreground">
          {selectedCount}/{editableItems.length} items selected
        </div>
      </div>

      <Separator />

      {/* Items list */}
      <ScrollArea className="h-[400px]">
        <div className="space-y-3">
          {editableItems.map((item, index) => (
            <div
              key={item.id}
              className={`flex items-start space-x-3 p-3 rounded-lg border transition-colors ${
                item.selected ? "bg-blue-50 border-blue-200" : "bg-gray-50 border-gray-200"
              }`}
            >
              {/* Drag handle */}
              <div className="mt-1 text-gray-400 cursor-move">
                <FontAwesomeIcon icon={faGripVertical} className="h-4 w-4" />
              </div>

              {/* Checkbox */}
              <Checkbox
                id={`item-${item.id}`}
                checked={item.selected}
                onCheckedChange={(checked) => handleItemSelect(index, checked as boolean)}
                className="mt-1"
              />

              {/* Content */}
              <div className="flex-1 min-w-0">
                {item.isEditing ? (
                  <div className="space-y-2">
                    <Input
                      value={item.name}
                      onChange={(e) => handleEditItem(index, "name", e.target.value)}
                      placeholder="Item name"
                      className="font-medium"
                    />
                    <Input
                      value={item.website}
                      onChange={(e) => handleEditItem(index, "website", e.target.value)}
                      placeholder="Website URL"
                      className="text-sm"
                    />
                  </div>
                ) : (
                  <>
                    <Label
                      htmlFor={`item-${item.id}`}
                      className="font-medium text-base cursor-pointer"
                    >
                      {item.name}
                    </Label>
                    <div className="flex items-center space-x-2 mt-1">
                      <a
                        href={item.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:text-blue-800 flex items-center space-x-1"
                      >
                        <span>{item.website}</span>
                        <FontAwesomeIcon icon={faExternalLinkAlt} className="h-3 w-3" />
                      </a>
                    </div>
                    {item.description && (
                      <p className="text-sm text-muted-foreground mt-1">
                        {item.description}
                      </p>
                    )}
                  </>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center space-x-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => toggleEditMode(index)}
                  className="h-8 w-8 p-0"
                >
                  <FontAwesomeIcon icon={faEdit} className="h-3 w-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemoveItem(index)}
                  className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                >
                  <FontAwesomeIcon icon={faTrash} className="h-3 w-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>

      <Separator />

      {/* Add new item section */}
      <div className="space-y-3">
        {showAddForm ? (
          <div className="space-y-3 p-3 border rounded-lg bg-gray-50">
            <h4 className="font-medium">Add Custom Item</h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="new-item-name">Item Name</Label>
                <Input
                  id="new-item-name"
                  value={newItemName}
                  onChange={(e) => setNewItemName(e.target.value)}
                  placeholder="Enter item name"
                />
              </div>
              <div>
                <Label htmlFor="new-item-website">Website URL</Label>
                <Input
                  id="new-item-website"
                  value={newItemWebsite}
                  onChange={(e) => setNewItemWebsite(e.target.value)}
                  placeholder="Enter website URL"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowAddForm(false);
                  setNewItemName("");
                  setNewItemWebsite("");
                }}
              >
                Cancel
              </Button>
              <Button size="sm" onClick={handleAddItem}>
                Add Item
              </Button>
            </div>
          </div>
        ) : (
          <Button
            variant="outline"
            onClick={() => setShowAddForm(true)}
            className="w-full"
          >
            Add Custom Item
          </Button>
        )}
      </div>

      {/* Generation section */}
      <div className="flex items-center justify-between p-3 border rounded-lg bg-blue-50">
        <div>
          <p className="font-medium">Ready to generate your listicle?</p>
          <p className="text-sm text-muted-foreground">
            {selectedCount} items selected • Minimum 3 required
          </p>
        </div>
        <Button
          onClick={handleGenerate}
          disabled={selectedCount < 3}
          className="bg-black hover:bg-black/90"
        >
          Generate Article
          <FontAwesomeIcon icon={faArrowRight} className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}